import type { ActivityType } from './activities';
export type { ActivityType } from './activities';

export type TalentLoadout = Record<string, number>;

export interface TalentDefinition {
  id: string;
  title: string;
  description: string;
  cost: number;
  maxPoints: number;
  effectLabel: string;
  highlight?: boolean;
}

export interface TalentLoadoutValidationResult {
  valid: boolean;
  errors: string[];
  talentLoadout: TalentLoadout;
  spentTalentPoints: number;
}

export interface ActivityTalentRequirement {
  talentId: string;
  minQty: number;
  bonusPointsPerRank: number;
}

export const BIG_SWING_TALENT_ID = 'big-swing';
export const BIG_SWING_MIN_BASE_POINTS = 50;
export const BIG_SWING_BONUS_POINTS_PER_RANK = 10;
export const CIRCUITRY_TALENT_ID = 'circuitry';
export const CIRCUITRY_BONUS_POINTS_PER_RANK = 4;
export const SECOND_WIND_TALENT_ID = 'second-wind';
export const SECOND_WIND_BONUS_POINTS_PER_RANK = 10;
export const CATCH_YOUR_BREATH_TALENT_ID = 'catch-your-breath';
export const CATCH_YOUR_BREATH_BONUS_POINTS_PER_RANK = 5;
export const CATCH_YOUR_BREATH_MIN_GAP_MS = 30 * 60 * 1000;
export const MOMENTUM_TALENT_ID = 'momentum';
export const MOMENTUM_BONUS_POINTS_PER_RANK = 2;
export const MOMENTUM_MAX_GAP_MS = 30 * 60 * 1000;

export const talentDefinitions: TalentDefinition[] = [
  {
    id: 'double-pushups',
    title: 'Double pushups',
    description: 'Doubles the score of pushups.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x pushup points',
  },
  {
    id: 'double-pull-ups',
    title: 'Double pull ups',
    description: 'Doubles the points scored by pull ups.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x pull up points',
  },
  {
    id: 'double-squats',
    title: 'Double squats',
    description: 'Doubles the score of squats.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x squat points',
  },
  {
    id: 'double-burpees',
    title: 'Double burpees',
    description: 'Doubles the score of burpees.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x burpee points',
  },
  {
    id: 'double-sit-ups',
    title: 'Double sit-ups',
    description: 'Doubles the score of sit-ups.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x sit-up points',
  },
  {
    id: 'double-jumping-jacks',
    title: 'Double jumping jacks',
    description: 'Doubles the score of jumping jacks.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x jumping jack points',
  },
  {
    id: 'double-plank',
    title: 'Double plank',
    description: 'Doubles the base points earned from seconds of plank.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x base points for plank seconds',
  },
  {
    id: 'double-hang',
    title: 'Double hang',
    description: 'Doubles the base points earned from seconds hanging from a bar.',
    cost: 10,
    maxPoints: 1,
    effectLabel: '2x base points for hang seconds',
  },
  {
    id: 'pull-up-pump',
    title: 'Pull up pump',
    description: 'Gain +10 points per rank on your first pull up activity of at least 2 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 first 2+ pull up points per rank',
  },
  {
    id: 'pushup-pump',
    title: 'Pushup pump',
    description: 'Gain +10 points per rank on your first pushup activity of at least 5 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 first 5+ pushup points per rank',
  },
  {
    id: 'squat-pump',
    title: 'Squat pump',
    description: 'Gain +10 points per rank on your first squat activity of at least 10 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 first 10+ squat points per rank',
  },
  {
    id: 'burpee-pump',
    title: 'Burpee pump',
    description: 'Gain +10 points per rank on your first burpee activity of at least 3 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 first 3+ burpee points per rank',
  },
  {
    id: 'sit-up-pump',
    title: 'Sit-up pump',
    description: 'Gain +10 points per rank on your first sit-up activity of at least 10 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 first 10+ sit-up points per rank',
  },
  {
    id: 'jumping-jack-pump',
    title: 'Jumping jack pump',
    description: 'Gain +10 points per rank on your first jumping jack activity of at least 10 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 first 10+ jumping jack points per rank',
  },
  {
    id: 'plank-pump',
    title: 'Plank pump',
    description: 'Gain +10 points per rank on your first plank activity of at least 30 seconds in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 points per rank on first 30+ seconds of plank',
  },
  {
    id: 'hang-pump',
    title: 'Hang pump',
    description: 'Gain +10 points per rank on your first hang activity of at least 30 seconds hanging from a bar in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+10 points per rank on first 30+ seconds of hang',
  },
  {
    id: 'pushup-max',
    title: 'Pushup max',
    description: 'Gain +20 points per rank on your first pushup activity of at least 25 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 first 25+ pushup points per rank',
  },
  {
    id: 'pull-up-max',
    title: 'Pull up max',
    description: 'Gain +20 points per rank on your first pull up activity of at least 10 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 first 10+ pull up points per rank',
  },
  {
    id: 'squat-max',
    title: 'Squat max',
    description: 'Gain +20 points per rank on your first squat activity of at least 40 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 first 40+ squat points per rank',
  },
  {
    id: 'burpee-max',
    title: 'Burpee max',
    description: 'Gain +20 points per rank on your first burpee activity of at least 15 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 first 15+ burpee points per rank',
  },
  {
    id: 'sit-up-max',
    title: 'Sit-up max',
    description: 'Gain +20 points per rank on your first sit-up activity of at least 35 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 first 35+ sit-up points per rank',
  },
  {
    id: 'jumping-jack-max',
    title: 'Jumping jack max',
    description: 'Gain +20 points per rank on your first jumping jack activity of at least 40 reps in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 first 40+ jumping jack points per rank',
  },
  {
    id: 'plank-max',
    title: 'Plank max',
    description: 'Gain +20 points per rank on your first plank activity of at least 60 seconds in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 points per rank on first 60+ seconds of plank',
  },
  {
    id: 'hang-max',
    title: 'Hang max',
    description: 'Gain +20 points per rank on your first hang activity of at least 60 seconds hanging from a bar in each workout.',
    cost: 1,
    maxPoints: 5,
    effectLabel: '+20 points per rank on first 60+ seconds of hang',
  },
  {
    id: 'warmup-streak',
    title: 'Warmup Streak',
    description: 'Gain +5 points per rank on your first logged activity in each workout.',
    cost: 1,
    maxPoints: 10,
    effectLabel: '+5 points per rank on first activity',
  },
  {
    id: BIG_SWING_TALENT_ID,
    title: 'Big Swing',
    description: `Gain +${BIG_SWING_BONUS_POINTS_PER_RANK} points per rank when a single submission is worth at least ${BIG_SWING_MIN_BASE_POINTS} base points before talents.`,
    cost: 1,
    maxPoints: 10,
    effectLabel: `+${BIG_SWING_BONUS_POINTS_PER_RANK} points per rank at ${BIG_SWING_MIN_BASE_POINTS}+ base points`,
  },
  {
    id: CIRCUITRY_TALENT_ID,
    title: 'Circuitry',
    description: `Gain +${CIRCUITRY_BONUS_POINTS_PER_RANK} points per rank on your first qualifying activity of each type in a workout. Uses the same activity minimums as Pump talents.`,
    cost: 1,
    maxPoints: 10,
    effectLabel: `+${CIRCUITRY_BONUS_POINTS_PER_RANK} points per rank for each new activity type`,
  },
  {
    id: SECOND_WIND_TALENT_ID,
    title: 'Second Wind',
    description: `Gain +${SECOND_WIND_BONUS_POINTS_PER_RANK} points per rank on your first activity after the workout passes its halfway point.`,
    cost: 1,
    maxPoints: 10,
    effectLabel: `+${SECOND_WIND_BONUS_POINTS_PER_RANK} points per rank after halfway`,
  },
  {
    id: CATCH_YOUR_BREATH_TALENT_ID,
    title: 'Catch Your Breath',
    description: `Gain +${CATCH_YOUR_BREATH_BONUS_POINTS_PER_RANK} points per rank when your qualifying activity is at least 30 minutes after your previous submission.`,
    cost: 1,
    maxPoints: 10,
    effectLabel: `+${CATCH_YOUR_BREATH_BONUS_POINTS_PER_RANK} points per rank after 30 minutes`,
  },
  {
    id: MOMENTUM_TALENT_ID,
    title: 'Momentum',
    description: `Gain +${MOMENTUM_BONUS_POINTS_PER_RANK} points per rank when your qualifying activity is within 30 minutes of your previous submission.`,
    cost: 1,
    maxPoints: 10,
    effectLabel: `+${MOMENTUM_BONUS_POINTS_PER_RANK} points per rank within 30 minutes`,
  },
  {
    id: 'circuit-work',
    title: 'Circuit Work',
    description: 'Gain +10 points per rank when you log your 3rd different activity type in a workout.',
    cost: 1,
    maxPoints: 10,
    effectLabel: '+10 points per rank at 3 activity types',
  },
  {
    id: 'super-circuit',
    title: 'Super Circuit',
    description: 'Gain +10 points per rank when you log your 6th different activity type in a workout.',
    cost: 1,
    maxPoints: 10,
    effectLabel: '+10 points per rank at 6 activity types',
  },
  {
    id: 'rally-call',
    title: 'Rally Call',
    description: 'Gain +10 points per rank if you are the first non-creator to log activity in a workout.',
    cost: 1,
    maxPoints: 10,
    effectLabel: '+10 points per rank as first teammate',
  },
  {
    id: 'rallier',
    title: 'Rallier',
    description: 'Gain +10 points per rank on your first creator activity after another player logs activity.',
    cost: 1,
    maxPoints: 10,
    effectLabel: '+10 points per rank after teammate joins in',
  },
];

const talentDefinitionById = new Map(talentDefinitions.map((talent) => [talent.id, talent]));
const TALENT_POINT_EPSILON = 0.000001;

export const PULL_UP_PUMP_TALENT_ID = 'pull-up-pump';
export const CIRCUIT_WORK_TALENT_ID = 'circuit-work';
export const SUPER_CIRCUIT_TALENT_ID = 'super-circuit';
export const RALLY_CALL_TALENT_ID = 'rally-call';
export const RALLIER_TALENT_ID = 'rallier';
export const WARMUP_STREAK_TALENT_ID = 'warmup-streak';

const doubleTalentByActivity: Record<ActivityType, string> = {
  Plank: 'double-plank',
  Hang: 'double-hang',
  Pushups: 'double-pushups',
  'Pull Ups': 'double-pull-ups',
  Squats: 'double-squats',
  Burpees: 'double-burpees',
  'Sit-ups': 'double-sit-ups',
  'Jumping Jacks': 'double-jumping-jacks',
};

const pumpTalentByActivity: Record<ActivityType, ActivityTalentRequirement> = {
  Plank: { talentId: 'plank-pump', minQty: 30, bonusPointsPerRank: 10 },
  Hang: { talentId: 'hang-pump', minQty: 30, bonusPointsPerRank: 10 },
  Pushups: { talentId: 'pushup-pump', minQty: 5, bonusPointsPerRank: 10 },
  'Pull Ups': { talentId: PULL_UP_PUMP_TALENT_ID, minQty: 2, bonusPointsPerRank: 10 },
  Squats: { talentId: 'squat-pump', minQty: 10, bonusPointsPerRank: 10 },
  Burpees: { talentId: 'burpee-pump', minQty: 3, bonusPointsPerRank: 10 },
  'Sit-ups': { talentId: 'sit-up-pump', minQty: 10, bonusPointsPerRank: 10 },
  'Jumping Jacks': { talentId: 'jumping-jack-pump', minQty: 10, bonusPointsPerRank: 10 },
};

const maxTalentByActivity: Record<ActivityType, ActivityTalentRequirement> = {
  Plank: { talentId: 'plank-max', minQty: 60, bonusPointsPerRank: 20 },
  Hang: { talentId: 'hang-max', minQty: 60, bonusPointsPerRank: 20 },
  Pushups: { talentId: 'pushup-max', minQty: 25, bonusPointsPerRank: 20 },
  'Pull Ups': { talentId: 'pull-up-max', minQty: 10, bonusPointsPerRank: 20 },
  Squats: { talentId: 'squat-max', minQty: 40, bonusPointsPerRank: 20 },
  Burpees: { talentId: 'burpee-max', minQty: 15, bonusPointsPerRank: 20 },
  'Sit-ups': { talentId: 'sit-up-max', minQty: 35, bonusPointsPerRank: 20 },
  'Jumping Jacks': { talentId: 'jumping-jack-max', minQty: 40, bonusPointsPerRank: 20 },
};

export function getTalentDefinition(talentId: string): TalentDefinition | undefined {
  return talentDefinitionById.get(talentId);
}

export function getDoubleTalentIdForActivity(type: ActivityType): string {
  return doubleTalentByActivity[type];
}

export function getPumpTalentRequirementForActivity(type: ActivityType): ActivityTalentRequirement {
  return pumpTalentByActivity[type];
}

export function getMaxTalentRequirementForActivity(type: ActivityType): ActivityTalentRequirement {
  return maxTalentByActivity[type];
}

export function normalizeTalentLoadout(value: unknown): TalentLoadout {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<TalentLoadout>((acc, [talentId, rank]) => {
    const talent = getTalentDefinition(talentId);
    const numericRank = Number(rank);

    if (talent && Number.isInteger(numericRank) && numericRank > 0 && numericRank <= talent.maxPoints) {
      acc[talentId] = numericRank;
    }

    return acc;
  }, {});
}

export function setTalentRankInLoadout(talentLoadout: TalentLoadout, talentId: string, rank: number): TalentLoadout {
  const nextLoadout = { ...talentLoadout };

  if (Number.isFinite(rank) && rank > 0) {
    nextLoadout[talentId] = rank;
  } else {
    delete nextLoadout[talentId];
  }

  return normalizeTalentLoadout(nextLoadout);
}

export function getTalentLoadoutCost(talentLoadout: TalentLoadout): number {
  return Object.entries(talentLoadout).reduce((total, [talentId, rank]) => {
    const talent = getTalentDefinition(talentId);
    if (!talent) return total;
    return total + talent.cost * rank;
  }, 0);
}

export function validateTalentLoadout(value: unknown, availableTalentPoints: number): TalentLoadoutValidationResult {
  const errors: string[] = [];
  const talentLoadout: TalentLoadout = {};

  if (value === undefined || value === null) {
    return {
      valid: Number.isFinite(availableTalentPoints) && availableTalentPoints >= 0,
      errors: Number.isFinite(availableTalentPoints) && availableTalentPoints >= 0 ? [] : ['Talent point budget is invalid.'],
      talentLoadout,
      spentTalentPoints: 0,
    };
  }

  if (typeof value !== 'object') {
    return {
      valid: false,
      errors: ['Talent loadout must be an object.'],
      talentLoadout,
      spentTalentPoints: 0,
    };
  }

  Object.entries(value as Record<string, unknown>).forEach(([talentId, rank]) => {
    const talent = getTalentDefinition(talentId);
    const numericRank = Number(rank);

    if (!talent) {
      errors.push(`Unknown talent: ${talentId}.`);
      return;
    }

    if (!Number.isInteger(numericRank) || numericRank < 0) {
      errors.push(`${talent.title} rank must be a whole number.`);
      return;
    }

    if (numericRank > talent.maxPoints) {
      errors.push(`${talent.title} rank cannot exceed ${talent.maxPoints}.`);
      return;
    }

    if (numericRank > 0) {
      talentLoadout[talentId] = numericRank;
    }
  });

  const spentTalentPoints = getTalentLoadoutCost(talentLoadout);

  if (!Number.isFinite(availableTalentPoints) || availableTalentPoints < 0) {
    errors.push('Talent point budget is invalid.');
  } else if (spentTalentPoints - availableTalentPoints > TALENT_POINT_EPSILON) {
    errors.push(`Talent loadout costs ${spentTalentPoints}, but only ${availableTalentPoints} talent points are available.`);
  }

  return {
    valid: errors.length === 0,
    errors,
    talentLoadout,
    spentTalentPoints,
  };
}

export function canAffordTalentLoadout(value: unknown, availableTalentPoints: number): boolean {
  return validateTalentLoadout(value, availableTalentPoints).valid;
}
